import { useMemo } from "react"
import { useNavigate } from "react-router"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { SkillBadge } from "@/components/ui/skill-badge"
import { ArrowRight, Brain, Briefcase } from "lucide-react"
import { StudentStore } from "@/hooks/stores/StudentStore"
import { getAllNodes } from "@/data/career-tree"

// ─── Career meta lookup ───────────────────────────────────────────────────────

const CAREER_META: { keywords: string[]; skills: string[] }[] = [
  { keywords: ["software", "developer", "engineer", "programmer", "coding", "web"],   skills: ["Programming", "Problem Solving", "Teamwork"] },
  { keywords: ["doctor", "physician", "medicine", "medical"],                         skills: ["Empathy", "Analytical Thinking", "Decision Making"] },
  { keywords: ["nurse", "nursing", "health"],                                         skills: ["Empathy", "Attention to Detail", "Medical Knowledge"] },
  { keywords: ["chef", "cook", "culinary", "food"],                                   skills: ["Creativity", "Time Management", "Leadership"] },
  { keywords: ["design", "graphic", "art", "creative", "visual", "animation"],       skills: ["Creativity", "Visual Communication", "Software"] },
  { keywords: ["architect", "architecture", "build", "construction"],                 skills: ["Design Skills", "Technical Drawing", "Project Mgmt"] },
  { keywords: ["lawyer", "law", "legal", "attorney", "advocate"],                     skills: ["Reasoning", "Communication", "Research"] },
  { keywords: ["teacher", "education", "professor", "lecturer", "tutor"],             skills: ["Communication", "Patience", "Subject Mastery"] },
  { keywords: ["business", "entrepreneur", "management", "manager", "executive"],     skills: ["Leadership", "Strategy", "Communication"] },
  { keywords: ["finance", "accountant", "banker", "economics", "investment"],         skills: ["Numerical Analysis", "Attention to Detail", "Strategy"] },
  { keywords: ["science", "researcher", "biologist", "chemist", "physicist"],         skills: ["Analytical Thinking", "Mathematics", "Research"] },
  { keywords: ["environment", "agriculture", "farming", "ecology", "conservation"],   skills: ["Problem Solving", "Fieldwork", "Biology"] },
  { keywords: ["technology", "it", "data", "cyber", "ai", "machine learning"],        skills: ["Technical Skills", "Critical Thinking", "Innovation"] },
  { keywords: ["music", "musician", "performer", "artist", "entertainer"],            skills: ["Creativity", "Performance", "Discipline"] },
  { keywords: ["international", "diplomat", "global", "languages", "relations"],      skills: ["Communication", "Cultural Awareness", "Languages"] },
]

const DEFAULT_SKILLS = ["Critical Thinking", "Communication", "Adaptability"]

function getSkills(careerName: string): string[] {
  const lower = careerName.toLowerCase()
  return CAREER_META.find(m => m.keywords.some(k => lower.includes(k)))?.skills ?? DEFAULT_SKILLS
}

function scoreNode(
  node: { traits: { traitId: string; minimumThreshold: number }[] },
  profile: Record<string, number>,
): number {
  if (!node.traits?.length) return 0
  let raw = 0, max = 0
  for (const { traitId, minimumThreshold } of node.traits) {
    raw += ((profile[traitId] ?? 0) / 100) * minimumThreshold
    max += minimumThreshold
  }
  return max > 0 ? Math.round((raw / max) * 100) : 0
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CareerSpotlight() {
  const navigate = useNavigate()
  const traitProfile = StudentStore((s) => s.student?.traitProfile ?? null)

  const topCareer = useMemo(() => {
    if (!traitProfile) return null
    const allNodes = getAllNodes()
    const fieldById = Object.fromEntries(
      allNodes.filter(n => n.level === "field").map(n => [n.nodeId, n])
    )
    let best: { name: string; match: number } | null = null
    for (const n of allNodes) {
      if (n.level !== "specialization") continue
      const scoringNode = n.traits?.length ? n : (fieldById[n.parentId] ?? n)
      const match = scoreNode(scoringNode as any, traitProfile as Record<string, number>)
      if (!best || match > best.match) best = { name: n.name, match }
    }
    return best
  }, [traitProfile])

  if (!topCareer) {
    return (
      <Card className="border-slate-200 shadow-sm sticky top-10">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mt-4">
            <Brain className="w-8 h-8 text-indigo-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mt-4">Career Spotlight</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-xs mx-auto">
            Complete the psychometric assessment to unlock your personalised career spotlight.
          </p>
          <Button
            onClick={() => navigate("/psychometric-assessment")}
            className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
          >
            Take Assessment
            <ArrowRight className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>
    )
  }

  const skills = getSkills(topCareer.name)

  return (
    <Card className="border-slate-200 shadow-sm sticky top-10">
      <CardContent className="p-6 text-center">
        <div className="w-20 h-20 rounded-2xl mx-auto bg-indigo-50 flex items-center justify-center mt-4">
          <Briefcase className="w-10 h-10 text-indigo-500" />
        </div>

        <h3 className="text-base font-bold text-slate-500 mt-4">Career Spotlight</h3>
        <p className="text-xl font-black text-indigo-600 mt-1 leading-tight">{topCareer.name}</p>
        <span className="inline-block mt-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100">
          {topCareer.match}% trait match
        </span>

        <div className="mt-5 text-left">
          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Key Skills</p>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill) => (
              <SkillBadge key={skill} skill={skill} />
            ))}
          </div>
        </div>

        <Button
          onClick={() => navigate(`/career-learning-path?career=${encodeURIComponent(topCareer.name)}`)}
          className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
        >
          Explore {topCareer.name.split(" ").slice(0, 2).join(" ")} Path
          <ArrowRight className="w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  )
}
